import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Search, Menu, Heart, LogOut, User, LayoutDashboard, Home as HomeIcon, Wallet, Gift, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { NotificationsBell } from "@/components/NotificationsBell";
import { BrandLogo } from "@/components/BrandLogo";
import { ContactStrip, useSiteConfig } from "@/components/SiteConfig";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";

export function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const homeFor = (u) => {
    if (!u) return "/";
    if (u.role === "master" || u.role === "admin") return "/admin";
    if (u.role === "professional") return "/painel/profissional";
    if (u.role === "clinic") return "/painel/clinica";
    return "/app/associado";
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-6">
        <BrandLogo/>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-700">
          <NavLink to="/buscar" data-testid="nav-buscar" className={({isActive})=>isActive?"text-[#0D7C66]":"hover:text-[#0D7C66]"}>Buscar</NavLink>
          <NavLink to="/beneficios" data-testid="nav-beneficios" className={({isActive})=>isActive?"text-[#0D7C66]":"hover:text-[#0D7C66]"}>Benefícios</NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={({isActive})=>isActive?"text-[#0D7C66]":"hover:text-[#0D7C66]"}>Blog</NavLink>
          <NavLink to="/noticias" data-testid="nav-noticias" className={({isActive})=>isActive?"text-[#0D7C66]":"hover:text-[#0D7C66]"}>Notícias</NavLink>
          <NavLink to="/seja-associado" data-testid="nav-seja-associado" className={({isActive})=>isActive?"text-[#0D7C66]":"hover:text-[#0D7C66]"}>Seja Associado</NavLink>
          <NavLink to="/seja-parceiro" data-testid="nav-seja-parceiro" className={({isActive})=>isActive?"text-[#0D7C66]":"hover:text-[#0D7C66]"}>Seja Parceiro</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <NotificationsBell/>
              <Button data-testid="header-dashboard-btn" variant="ghost" size="sm" onClick={()=>nav(homeFor(user))}>
                <LayoutDashboard className="w-4 h-4 mr-1"/> Painel
              </Button>
              <Button data-testid="header-logout-btn" variant="ghost" size="sm" onClick={logout}>
                <LogOut className="w-4 h-4"/>
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="header-login-btn" variant="ghost" size="sm" onClick={()=>nav("/login")}>Entrar</Button>
              <Button data-testid="header-signup-btn" size="sm" className="bg-[#0D7C66] hover:bg-[#074E41]" onClick={()=>nav("/cadastro")}>Criar conta</Button>
            </>
          )}
          <button data-testid="header-menu-toggle" className="md:hidden p-2" onClick={()=>setOpen(!open)}>{open?<X/>:<Menu/>}</button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-2 text-sm">
          {[["/buscar","Buscar"],["/beneficios","Benefícios"],["/blog","Blog"],["/seja-associado","Seja Associado"],["/seja-parceiro","Seja Parceiro"]].map(([to,l])=>(
            <Link key={to} to={to} onClick={()=>setOpen(false)} className="block py-2">{l}</Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const cfg = useSiteConfig();
  return (
    <footer className="bg-[#074E41] text-slate-200 mt-24">
      <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-10">
        <div>
          <BrandLogo variant="footer"/>
          <p className="mt-3 text-sm text-slate-300">Conectando você aos profissionais e serviços de saúde da Baixada Santista.</p>
          <div className="mt-4"><ContactStrip/></div>
          {cfg?.brand?.domain && <div className="mt-3 text-xs text-slate-400" data-testid="footer-domain">{cfg.brand.domain}</div>}
          {cfg?.payments?.pix_key && <div className="mt-3 text-xs text-slate-400">Pix ({cfg.payments.pix_key_type}): <span className="font-mono text-slate-200">{cfg.payments.pix_key}</span></div>}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Plataforma</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/buscar">Buscar</Link></li>
            <li><Link to="/beneficios">Benefícios</Link></li>
            <li><Link to="/blog">Blog</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Comercial</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/seja-associado">Seja Associado</Link></li>
            <li><Link to="/seja-parceiro">Seja Parceiro</Link></li>
            <li><Link to="/contato">Contato</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/termos">Termos de Uso</Link></li>
            <li><Link to="/privacidade">Privacidade & LGPD</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-400">© {new Date().getFullYear()} GIRO+SAÚDE • Baixada Santista • SP</div>
    </footer>
  );
}

export function AssociateMobileNav() {
  const items = [
    {to:"/app/associado", label:"Início", icon:HomeIcon, testid:"mob-inicio"},
    {to:"/app/associado/buscar", label:"Buscar", icon:Search, testid:"mob-buscar"},
    {to:"/app/associado/beneficios", label:"Benefícios", icon:Gift, testid:"mob-beneficios"},
    {to:"/app/associado/carteira", label:"Carteira", icon:Wallet, testid:"mob-carteira"},
    {to:"/app/associado/perfil", label:"Perfil", icon:User, testid:"mob-perfil"},
  ];
  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 md:hidden">
      <div className="grid grid-cols-5">
        {items.map(i => (
          <NavLink key={i.to} to={i.to} end data-testid={i.testid}
            className={({isActive})=>`flex flex-col items-center py-2 text-[11px] ${isActive?"text-[#0D7C66]":"text-slate-500"}`}>
            <i.icon className="w-5 h-5 mb-0.5"/>{i.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header/>
      <main className="flex-1">{children}</main>
      <Footer/>
      <FloatingWhatsApp/>
    </div>
  );
}
